import React, { useRef } from "react";
import { FaArrowDownShortWide } from "react-icons/fa6";
import html2canvas from "html2canvas"

export type FallbackProps = {
  error: Error | null;
  info: { componentStack: string } | null;
  online: boolean;
  onRetry: () => void;
  onReload?: () => void;
};

export function DefaultFallback({
  error,
  info,
  online,
  onRetry,
  onReload,
}: FallbackProps) {
  const [copied, setCopied] = React.useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  const details = React.useMemo(() => {
    const payload = {
      message: error?.message,
      name: error?.name,
      stack: error?.stack,
      componentStack: info?.componentStack,
      userAgent:
        typeof navigator !== "undefined" ? navigator.userAgent : undefined,
      online,
      timestamp: new Date().toISOString(),
    };
    return JSON.stringify(payload, null, 2);
  }, [error, info, online]);

  async function copy() {
    try {
      await navigator.clipboard.writeText(details);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { }
  }

  async function handleScreenshotAndSave() {
    try {
      if (!cardRef.current) return;
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: "#ffffff",
        scale: 2,
        useCORS: true,
        logging: false,
      });

      // Image download
      const dataUrl = canvas.toDataURL("image/png");
      const link = document.createElement("a");
      link.href = dataUrl;
      link.download = `${new Date().toISOString()}-${Date.now()}-error-details-SS.png`;
      link.click();

      // Details as TXT
      const blob = new Blob([details], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const textLink = document.createElement("a");
      textLink.href = url;
      textLink.download = `${new Date().toISOString()}-${Date.now()}-error-details-Text.txt`;
      textLink.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Screenshot or save failed:", err);
    }
  }

  return (
    <div className="min-h-[40vh] w-full grid place-items-center p-6">
      <div
        ref={cardRef}
        className="w-full max-w-xl rounded-2xl border border-gray-200 shadow-sm p-6 bg-white dark:bg-zinc-900 dark:border-zinc-800">
        <div className="flex items-start gap-3">
          <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
            {/* Alert icon */}
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-6 w-6 text-red-600 dark:text-red-400">
              <path fillRule="evenodd" d="M10.29 3.86a2.25 2.25 0 0 1 3.42 0l7.5 8.5a2.25 2.25 0 0 1-1.71 3.74H4.5A2.25 2.25 0 0 1 2.79 12.36l7.5-8.5Zm1.71 4.39a.75.75 0 0 1 .75.75v3.75a.75.75 0 0 1-1.5 0V9a.75.75 0 0 1 .75-.75Zm0 7.5a1 1 0 1 0 0 2 1 1 0 0 0 0-2Z" clipRule="evenodd" />
            </svg>
          </span>
          <div className="flex-1">
            <h2 className="text-lg font-semibold tracking-tight">Something went wrong</h2>
            <p className="mt-1 text-sm text-gray-600 dark:text-zinc-400">
              {online
                ? "An unexpected error occurred. You can try again."
                : "You're currently offline. Some features may not work until you're back online."}
            </p>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={onRetry}
            className="inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-medium border border-gray-300 bg-white hover:bg-gray-50 active:scale-[0.99] transition dark:bg-zinc-800 dark:border-zinc-700"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="h-4 w-4">
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992V4.355M20.268 10.73A8.25 8.25 0 1 1 3.75 12.75" />
            </svg>
            Try again
          </button>

          {onReload && (
            <button
              type="button"
              onClick={onReload}
              className="inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-medium border border-gray-300 bg-white hover:bg-gray-50 active:scale-[0.99] transition dark:bg-zinc-800 dark:border-zinc-700"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="h-4 w-4">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
              </svg>
              Reload page
            </button>
          )}

          {!online && (
            <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-1 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800">
              Offline mode
            </span>
          )}
        </div>
        <details className="mt-4 rounded-xl border border-gray-200 p-3 bg-gray-50 text-xs whitespace-pre-wrap dark:bg-zinc-800 dark:border-zinc-700">
          <summary className="cursor-pointer list-none font-medium flex justify-between basis-full w-full ">
            Error details <FaArrowDownShortWide size={18} />
          </summary>

          <pre className="mt-2 overflow-auto max-h-64">{details}</pre>
          <div className="mt-4 flex gap-2">
            <button
              type="button"
              onClick={copy}
              className=" mr-2 rounded-xl border px-3 py-1 text-xs border-gray-300 bg-white hover:bg-gray-100 dark:bg-zinc-900 dark:border-zinc-700"
            >
              {copied ? "Copied" : "Copy details"}
            </button>
            <button
              type="button"
              onClick={() => handleScreenshotAndSave()}
              className="rounded-xl border px-3 py-1 text-xs border-gray-300 bg-white hover:bg-gray-100 dark:bg-zinc-900 dark:border-zinc-700">
              Take a Screenshot and Save Error Details
            </button>
          </div>
        </details>
      </div>
    </div>
  );
}